import { z } from 'zod';
import { BOARD_TITLE_MAX_LENGTH, SHORT_LINK_MAX_LENGTH } from './board';
import { DUE_BUCKETS, boardFilterQuerySchema, dueBucketSchema } from './board-filter';
import { cardLabelSchema } from './label';
import { LIST_TITLE_MAX_LENGTH } from './list';
import { RANK_MAX_LENGTH } from './rank';
import { publicProfileSchema } from './user';
import { ulidSchema } from './id';

/**
 * Dashboard view contracts — block B53.
 *
 * The dashboard is current-state aggregation over one board. Tiles are board
 * configuration, while buckets are route-specific read models; none of these
 * widen the board, list, label or profile base schemas in place.
 */

export const DASHBOARD_DEFAULT_TILES = [
  { facet: 'list', chartType: 'bar' },
  { facet: 'member', chartType: 'bar' },
  { facet: 'label', chartType: 'bar' },
  { facet: 'due_date', chartType: 'bar' },
] as const;

export const DASHBOARD_FACETS = ['list', 'member', 'label', 'due_date'] as const;
export const DASHBOARD_CHART_TYPES = ['bar', 'pie'] as const;

export const dashboardFacetSchema = z.literal(DASHBOARD_FACETS);
export type DashboardFacet = z.infer<typeof dashboardFacetSchema>;

export const dashboardChartTypeSchema = z.literal(DASHBOARD_CHART_TYPES);
export type DashboardChartType = z.infer<typeof dashboardChartTypeSchema>;

export const boardDashboardQuerySchema = boardFilterQuerySchema;
export type BoardDashboardQuery = z.infer<typeof boardDashboardQuerySchema>;

export const dashboardTileSchema = z.object({
  id: ulidSchema,
  facet: dashboardFacetSchema,
  chartType: dashboardChartTypeSchema,
});
export type DashboardTile = z.infer<typeof dashboardTileSchema>;

export const dashboardTileCreateBodySchema = z.strictObject({
  facet: dashboardFacetSchema,
  chartType: dashboardChartTypeSchema,
});
export type DashboardTileCreateBody = z.infer<typeof dashboardTileCreateBodySchema>;

export const dashboardTileFacetPatchSchema = z.strictObject({
  facet: dashboardFacetSchema,
});
export type DashboardTileFacetPatch = z.infer<typeof dashboardTileFacetPatchSchema>;

export const dashboardTileChartTypePatchSchema = z.strictObject({
  chartType: dashboardChartTypeSchema,
});
export type DashboardTileChartTypePatch = z.infer<
  typeof dashboardTileChartTypePatchSchema
>;

export const dashboardBoardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(BOARD_TITLE_MAX_LENGTH),
  shortLink: z.string().max(SHORT_LINK_MAX_LENGTH),
  closed: z.boolean(),
});
export type DashboardBoardRef = z.infer<typeof dashboardBoardRefSchema>;

export const dashboardListBucketSchema = z.object({
  id: ulidSchema,
  title: z.string().max(LIST_TITLE_MAX_LENGTH),
  count: z.number().int().nonnegative(),
  list: z.object({
    id: ulidSchema,
    title: z.string().max(LIST_TITLE_MAX_LENGTH),
    position: z.string().max(RANK_MAX_LENGTH),
  }),
});
export type DashboardListBucket = z.infer<typeof dashboardListBucketSchema>;

export const dashboardMemberBucketSchema = z.object({
  id: z.union([z.templateLiteral(['member:', ulidSchema]), z.literal('member:none')]),
  title: z.string().min(1),
  count: z.number().int().nonnegative(),
  member: publicProfileSchema.nullable(),
});
export type DashboardMemberBucket = z.infer<typeof dashboardMemberBucketSchema>;

export const dashboardLabelBucketSchema = z.object({
  id: z.templateLiteral(['label:', ulidSchema]),
  title: z.string().min(1),
  count: z.number().int().nonnegative(),
  label: cardLabelSchema,
});
export type DashboardLabelBucket = z.infer<typeof dashboardLabelBucketSchema>;

export const dashboardDueDateBucketSchema = z.object({
  id: z.templateLiteral(['due:', dueBucketSchema]),
  title: z.string().min(1),
  count: z.number().int().nonnegative(),
  due: dueBucketSchema,
});
export type DashboardDueDateBucket = z.infer<typeof dashboardDueDateBucketSchema>;

export const dashboardBucketSchema = z.union([
  dashboardListBucketSchema,
  dashboardMemberBucketSchema,
  dashboardLabelBucketSchema,
  dashboardDueDateBucketSchema,
]);
export type DashboardBucket = z.infer<typeof dashboardBucketSchema>;

const dashboardAggregationBaseSchema = z.object({
  totalCards: z.number().int().nonnegative(),
  bucketCountsMayExceedTotalCards: z.boolean(),
});

export const dashboardAggregationSchema = z.discriminatedUnion('facet', [
  dashboardAggregationBaseSchema.extend({
    facet: z.literal('list'),
    buckets: z.array(dashboardListBucketSchema),
  }),
  dashboardAggregationBaseSchema.extend({
    facet: z.literal('member'),
    buckets: z.array(dashboardMemberBucketSchema),
  }),
  dashboardAggregationBaseSchema.extend({
    facet: z.literal('label'),
    buckets: z.array(dashboardLabelBucketSchema),
  }),
  dashboardAggregationBaseSchema.extend({
    facet: z.literal('due_date'),
    buckets: z.array(dashboardDueDateBucketSchema),
  }),
]);
export type DashboardAggregation = z.infer<typeof dashboardAggregationSchema>;

export const boardDashboardSchema = z.object({
  board: dashboardBoardRefSchema,
  tiles: z.array(dashboardTileSchema),
  aggregations: z.array(dashboardAggregationSchema),
});
export type BoardDashboard = z.infer<typeof boardDashboardSchema>;

export const dashboardDueDateBucketOrder = DUE_BUCKETS;
